import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { FlowsheetConstants } from "@/Flowsheets/FlowsheetConstants";
import { BooleanReviewedCareElement } from "@/Flowsheets/Models/CareElements/BooleanReviewedCareElement";
import { CareComponent } from "@/Flowsheets/Models/CareElements/CareComponent";
import { CareElementType } from "@/Flowsheets/Models/CareElements/CareElementType";
import { IntegerReviewedCareElement } from "@/Flowsheets/Models/CareElements/IntegerReviewedCareElement";
import CareComponentNotes from "./CareComponentParts/CareComponentNotes";
import CareElementDateReviewedNumber from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedNumber";
import { CareElementYesNoControl } from "./CareComponentParts/DateReviewedCareElementComponents/CareElementDateReviewedYesNoControl";
import { ReviewedCareElementContainer } from "./CareComponentParts/DateReviewedCareElementComponents/ReviewedDateContainer";
import { ICareComponentProps } from "./Types";

import styles from "./DiabetesBasicCareComponent.module.scss";

interface DiabetesBasicCareComponent extends CareComponent {
    CareElements: (BooleanReviewedCareElement | IntegerReviewedCareElement)[];
}

const DiabetesBasicCareComponent = React.memo(
    ({ isEditable = false, state, mutateState }: Readonly<ICareComponentProps<DiabetesBasicCareComponent>>): JSX.Element => {
        const { getText } = useTranslation();
        const coreCareElement = state?.CareElements?.toLookup(p => p.SubSectionName) ?? {};
        const keys = Object.keys(coreCareElement);

        if (!coreCareElement)
            return null;

        return (
            <div>
                {keys.map(key => {
                    const careElementsBySection = coreCareElement[key];
                    if (!careElementsBySection || careElementsBySection.length === 0)
                        return null;

                    return (
                        <ReviewedCareElementContainer key={`${FlowsheetConstants.DiabetesBasic.CareComponentName}_${key}`}>
                            <div className={`${styles.subsectionTitleLabel} ${styles.spanRight}`}>{getText(`Flowsheets_DiabetesBasicCareComponent_SubSectionName_${key}`)}</div>

                            {careElementsBySection.map((careElement => {
                                if (careElement.CareElementType === CareElementType.IntegerReviewed) {
                                    return (
                                        <CareElementDateReviewedNumber
                                            label={getText(`Flowsheets_DiabetesBasicCareComponentLabel_${careElement.Name}`)}
                                            key={`${careElement.Name}_${state.Id}`}
                                            element={careElement}
                                            onChange={(element: IntegerReviewedCareElement) => mutateState(state => {
                                                state.CareElements = [...state.CareElements];
                                                const index = state.CareElements.findIndex(p => p.Name === careElement.Name);
                                                if (index !== -1) {
                                                    state.CareElements[index] = element;
                                                }
                                            })}
                                            isEditable={isEditable}
                                            type="integer"
                                        />
                                    );
                                }
                                else if (careElement.CareElementType === CareElementType.BooleanReviewed) {
                                    return (
                                        <CareElementYesNoControl
                                            label={getText(`Flowsheets_DiabetesBasicCareComponentLabel_${careElement.Name}`)}
                                            key={`${careElement.Name}_${state.Id}`}
                                            element={careElement}
                                            onChange={careElement => {
                                                mutateState(state => {
                                                    state.CareElements = [...state.CareElements];
                                                    const index = state.CareElements.findIndex(p => p.Name === careElement.Name);
                                                    if (index !== -1) {
                                                        state.CareElements[index] = careElement;
                                                    }
                                                });
                                            }}
                                            isEditable={isEditable}
                                        />
                                    );
                                }
                                return null;
                            }))}
                        </ReviewedCareElementContainer>
                    );
                })}
                <CareComponentNotes
                    id={`diabetesBasicCareComponentNotes-${state.Id}`}
                    dataCy="DiabetesBasicCareComponentNotes"
                    value={state.Note}
                    isEditable={isEditable}
                    onTextChange={newValue => mutateState(s => s.Note = newValue)}
                />
            </div>
        );
    }
);

export { DiabetesBasicCareComponent };
